import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { TrendingUp, Clock, Repeat } from "lucide-react";

export const Perpetuals = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Understanding Perpetual Futures</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn what perpetual futures are and how they differ from spot trading</p>
        </div>

        {/* What Are Perpetuals */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Are Perpetual Futures?</h2>
          <p className="text-muted-foreground mb-6">
            Perpetual futures (or perpetual contracts, perpetuals, perps) are a type of derivative contract 
            that allows you to trade the price movement of an asset without owning the underlying asset itself. 
            Unlike traditional futures contracts, perpetual contracts have no expiration date—you can hold 
            them indefinitely.
          </p>
          
          <p className="text-muted-foreground mb-6">
            When you trade a perpetual contract on Nordex, you're not buying or selling actual Bitcoin, 
            Ethereum, or Solana. Instead, you're taking a position that profits (or loses) based on the 
            price movement of these assets.
          </p>
        </section>

        {/* Perpetuals vs Spot Trading */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Perpetuals vs Spot Trading</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border border-border rounded-lg">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-4 text-left font-semibold">Feature</th>
                  <th className="p-4 text-left font-semibold">Spot Trading</th>
                  <th className="p-4 text-left font-semibold">Perpetual Futures</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">Ownership</td>
                  <td className="p-4 text-sm text-muted-foreground">You own the actual asset</td>
                  <td className="p-4 text-sm text-muted-foreground">You don't own the asset (contract only)</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Leverage</td>
                  <td className="p-4 text-sm text-muted-foreground">No leverage (1x only)</td>
                  <td className="p-4 text-sm text-muted-foreground">Up to 100x leverage</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Short Selling</td>
                  <td className="p-4 text-sm text-muted-foreground">Difficult or impossible</td>
                  <td className="p-4 text-sm text-muted-foreground">Easy - just click "Short"</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Expiration</td>
                  <td className="p-4 text-sm text-muted-foreground">None (hold forever)</td>
                  <td className="p-4 text-sm text-muted-foreground">None (hold indefinitely)</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Funding Payments</td>
                  <td className="p-4 text-sm text-muted-foreground">None</td>
                  <td className="p-4 text-sm text-muted-foreground">Every 8 hours</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Liquidation Risk</td>
                  <td className="p-4 text-sm text-muted-foreground">None</td>
                  <td className="p-4 text-sm text-muted-foreground">Yes (if using leverage)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Key Features of Perpetuals</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Leverage Trading</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Perpetuals allow you to control a large position with a small amount of capital. 
                For example, with 10x leverage, you can control a $10,000 position with just $1,000 of collateral. 
                This amplifies both profits and losses.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-6 w-6 text-emerald-500 rotate-180" />
                </div>
                <h3 className="font-semibold text-lg">Easy Short Selling</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Unlike spot trading, perpetuals make it easy to profit from falling prices. 
                Simply click "Short" to open a position that profits when the price goes down. 
                No need to borrow assets or deal with complex mechanics.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Repeat className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg">No Expiration Date</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Unlike traditional futures contracts that expire on a specific date, perpetual contracts 
                never expire. You can hold your position for as long as you want (as long as you maintain 
                sufficient margin and pay funding fees).
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Clock className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="font-semibold text-lg">Funding Rate Mechanism</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                To keep the perpetual contract price aligned with the spot price, traders pay or receive 
                funding payments every 8 hours. If the perpetual price is higher than spot, longs pay shorts. 
                If lower, shorts pay longs.
              </p>
            </div>
          </div>
        </section>

        {/* How Price Tracking Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How Perpetuals Track Spot Price</h2>
          
          <p className="text-muted-foreground mb-6">
            Perpetual contracts are designed to closely track the spot price of the underlying asset. 
            This is achieved through the funding rate mechanism:
          </p>

          <div className="space-y-4 mb-6">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">When Perpetual Price {'>'} Spot Price</h3>
              <p className="text-sm text-muted-foreground">
                If the perpetual contract is trading above the spot price (premium), long position holders 
                pay funding to short position holders. This incentivizes shorts and discourages longs, 
                bringing the price back down.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">When Perpetual Price {'<'} Spot Price</h3>
              <p className="text-sm text-muted-foreground">
                If the perpetual contract is trading below the spot price (discount), short position holders 
                pay funding to long position holders. This incentivizes longs and discourages shorts, 
                bringing the price back up.
              </p>
            </div>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm">
              <strong>Example:</strong> If BTC spot price is $50,000 but the BTC perpetual is trading at $50,200, 
              there's a $200 premium. Long position holders would pay a funding rate to shorts every 8 hours 
              until the prices converge.
            </p>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Common Use Cases</h2>
          
          <div className="space-y-3">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Speculation</h3>
              <p className="text-sm text-muted-foreground">
                Trade price movements with leverage to amplify potential returns. Go long if you expect 
                prices to rise, short if you expect them to fall.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Hedging</h3>
              <p className="text-sm text-muted-foreground">
                If you hold spot assets (e.g., BTC in your wallet), you can open short perpetual positions 
                to hedge against price drops without selling your actual holdings.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Arbitrage</h3>
              <p className="text-sm text-muted-foreground">
                Advanced traders can exploit price differences between spot and perpetual markets or 
                between different exchanges to earn risk-free profits.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Funding Rate Farming</h3>
              <p className="text-sm text-muted-foreground">
                Some traders take positions primarily to earn funding payments when funding rates are 
                favorable, especially in neutral or delta-neutral strategies.
              </p>
            </div>
          </div>
        </section>

        {/* Important Considerations */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Important Considerations</h2>
          
          <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Leverage amplifies risk</strong> -- while leverage can amplify profits, it also amplifies losses. A 10% adverse price 
              movement with 10x leverage results in a 100% loss (liquidation).
            </li>
            <li>
              <strong className="text-foreground">Funding costs add up</strong> -- holding positions long-term means paying (or receiving) funding every 8 hours. 
              If funding rates are consistently against your position, costs can accumulate over time.
            </li>
            <li>
              <strong className="text-foreground">You don't own the asset</strong> -- perpetual contracts are purely financial instruments. You can't withdraw or transfer 
              the underlying asset because you don't actually own it -- you only own a contract.
            </li>
            <li>
              <strong className="text-foreground">Liquidation risk</strong> -- if the market moves against your position and your margin falls below the maintenance 
              requirement, your position will be automatically liquidated to prevent further losses.
            </li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Understanding the Interface", href: "/interface" }}
          next={{ title: "Leverage Trading Explained", href: "/leverage" }}
        />
      </div>
    </DocsLayout>
  );
};

